import { useEffect, useState, useMemo } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Award, Wine, ShoppingBag, ArrowRight, Plus, Minus, Truck, Store, Sparkles, FileDown } from "lucide-react";
import api from "../lib/api";
import { Button } from "../components/ui/button";
import UpsellStrip from "../components/UpsellStrip";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { toast } from "sonner";
import { isNewProduct } from "../lib/productUtils";

const CATEGORIES = [
  { id: "all", label: "Tous", icon: <Wine className="w-4 h-4" /> },
  { id: "medaille", label: "Médaillés 2026", icon: <Award className="w-4 h-4" /> },
  { id: "signature", label: "Signatures maison", icon: <Sparkles className="w-4 h-4" /> },
  { id: "coffret", label: "Coffrets cadeaux", icon: <ShoppingBag className="w-4 h-4" /> },
];

export default function Shop() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [active, setActive] = useState("all");
  const [cart, setCart] = useState({});  // { product_id: quantity }
  const [showCheckout, setShowCheckout] = useState(false);

  const [searchParams, setSearchParams] = useSearchParams();

  useEffect(() => {
    api.get("/products").then(({ data }) => {
      setProducts(data);
      const addSlug = searchParams.get("ajouter");
      const target = addSlug && data.find((p) => p.slug === addSlug);
      if (target) {
        setCart((c) => ({ ...c, [target.id]: Math.max(1, c[target.id] || 0) }));
        setSearchParams({}, { replace: true });
        toast.success(`${target.name.split("—")[0].trim()} ajouté à votre panier`);
        setTimeout(() => document.querySelector(`[data-testid="shop-product-${target.slug}"]`)?.scrollIntoView({ behavior: "smooth", block: "center" }), 300);
      }
    }).finally(() => setLoading(false));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const filtered = useMemo(() => {
    if (active === "all") return products;
    return products.filter((p) => p.category === active);
  }, [products, active]);

  const cartItems = useMemo(() => {
    return Object.entries(cart)
      .map(([pid, qty]) => {
        const p = products.find((x) => x.id === pid);
        return p && qty > 0 ? { ...p, quantity: qty, line_total: p.price * qty } : null;
      })
      .filter(Boolean);
  }, [cart, products]);

  const subtotal = cartItems.reduce((s, i) => s + i.line_total, 0);

  const updateQty = (pid, delta) => {
    setCart((c) => {
      const cur = c[pid] || 0;
      const next = Math.max(0, Math.min(10, cur + delta));
      const copy = { ...c };
      if (next === 0) delete copy[pid];
      else copy[pid] = next;
      return copy;
    });
  };

  return (
    <div data-testid="shop-page">
      {/* Hero */}
      <section className="relative overflow-hidden py-20 lg:py-28 border-b border-border">
        <div className="absolute inset-0">
          <img
            src="https://customer-assets.emergentagent.com/job_cheers-book/artifacts/9k7zo5gf_4306700533884434252.jpeg"
            alt="Cave des rhums arrangés"
            className="w-full h-full object-cover opacity-20"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-background via-background/85 to-background" />
        </div>
        <div className="relative max-w-7xl mx-auto px-6 lg:px-10">
          <div className="text-xs uppercase tracking-[0.35em] text-primary mb-5">· La Cave · Boutique en ligne ·</div>
          <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl leading-[1.05] max-w-4xl">
            Nos rhums arrangés
            <br />
            <span className="italic gold-text">en bouteille, livrés chez vous.</span>
          </h1>
          <p className="mt-6 text-lg text-muted-foreground max-w-2xl leading-relaxed">
            Les macérations maison de Saveurs Normandes, y compris nos cuvées primées au Frankfurt International Trophy 2026. Bouteilles de 70cl — à déguster avec modération.
          </p>
          <div className="mt-6 flex flex-wrap items-center gap-3">
            <div className="inline-flex items-center gap-2 rounded-full border border-primary/50 bg-primary/10 px-5 py-2 text-sm" data-testid="shop-free-shipping-banner">
              <Truck className="w-4 h-4 text-primary" />
              <span>Livraison <b className="gold-text">Colissimo Pro</b> partout en France · dès 9,59 €</span>
            </div>
            <a
              href={`${process.env.REACT_APP_BACKEND_URL}/api/products/catalogue.pdf`}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 rounded-full border border-foreground/30 hover:border-primary px-5 py-2 text-sm transition-colors"
              data-testid="shop-catalogue-pdf-link"
            >
              <FileDown className="w-4 h-4 text-primary" />
              Catalogue PDF imprimable
            </a>
          </div>
        </div>
      </section>

      {/* Filtres catégories */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-10">
        <div className="flex flex-wrap gap-3">
          {CATEGORIES.map((c) => (
            <button
              key={c.id}
              onClick={() => setActive(c.id)}
              className={`inline-flex items-center gap-2 rounded-full px-5 h-10 text-sm transition-colors border ${
                active === c.id
                  ? "bg-primary text-primary-foreground border-primary"
                  : "border-border hover:border-primary/60 text-muted-foreground hover:text-foreground"
              }`}
              data-testid={`shop-filter-${c.id}`}
            >
              {c.icon} {c.label}
            </button>
          ))}
        </div>
      </section>

      {/* Grille produits */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 pb-20">
        {loading ? (
          <div className="text-muted-foreground">Chargement…</div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
            {filtered.map((p) => (
              <ProductCard
                key={p.id}
                product={p}
                quantity={cart[p.id] || 0}
                onInc={() => updateQty(p.id, +1)}
                onDec={() => updateQty(p.id, -1)}
              />
            ))}
          </div>
        )}
      </section>

      {/* Panier flottant */}
      {cartItems.length > 0 && !showCheckout && (
        <div className="fixed bottom-6 left-0 right-0 z-40 flex justify-center pointer-events-none px-4">
          <button
            onClick={() => setShowCheckout(true)}
            className="pointer-events-auto inline-flex items-center gap-3 rounded-full bg-primary text-primary-foreground px-6 h-14 shadow-2xl hover:bg-primary/90 transition-colors"
            data-testid="shop-cart-toggle"
          >
            <ShoppingBag className="w-5 h-5" />
            <span className="font-display text-lg">Mon panier</span>
            <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-black/20 text-sm font-semibold">
              {cartItems.reduce((s, i) => s + i.quantity, 0)}
            </span>
            <span className="text-sm">· {subtotal.toFixed(2)} €</span>
            <ArrowRight className="w-4 h-4" />
          </button>
        </div>
      )}

      {showCheckout && (
        <CheckoutDrawer
          items={cartItems}
          subtotal={subtotal}
          onClose={() => setShowCheckout(false)}
          onSuccess={() => setCart({})}
        />
      )}
    </div>
  );
}

function ProductCard({ product, quantity, onInc, onDec }) {
  const isMedal = product.category === "medaille";
  const isNew = isNewProduct(product);
  return (
    <article
      className={`rounded-2xl overflow-hidden border bg-card hover-lift flex flex-col ${
        isMedal ? "border-primary/40" : "border-border"
      }`}
      data-testid={`shop-product-${product.slug}`}
    >
      {/* Image ou placeholder */}
      <div className="relative aspect-[4/5] overflow-hidden">
        {isNew && <div className="ribbon-new" data-testid={`shop-new-ribbon-${product.slug}`}>Nouveauté</div>}
        {product.image ? (
          <img src={product.image} alt={product.name} className="w-full h-full object-cover" />
        ) : (
          <div className="absolute inset-0 flex flex-col items-center justify-center text-muted-foreground bg-gradient-to-br from-card via-background to-card border-b border-border">
            <Wine className="w-16 h-16 mb-3 text-primary/40" strokeWidth={1.2} />
            <div className="text-[11px] uppercase tracking-[0.3em] text-primary/70">Visuel à venir</div>
            <div className="font-display text-xl mt-2 px-4 text-center">{product.name.split("—")[0].trim()}</div>
          </div>
        )}
        {isMedal && (
          <div className="absolute top-3 left-3 inline-flex items-center gap-1.5 rounded-full bg-black/70 backdrop-blur px-3 py-1 text-[10px] uppercase tracking-[0.25em] text-primary">
            <Award className="w-3 h-3" /> Primé 2026
          </div>
        )}
      </div>

      <div className="p-6 flex flex-col flex-1">
        <h3 className="font-display text-2xl mb-1">{product.name}</h3>
        {product.tagline && (
          <div className="text-xs uppercase tracking-[0.2em] text-primary/80 mb-3">{product.tagline}</div>
        )}
        <p className="text-sm text-muted-foreground leading-relaxed mb-3 flex-1">{product.description}</p>
        <Link to={`/boutique/${product.slug}`} className="inline-flex items-center gap-1.5 text-xs uppercase tracking-[0.2em] text-primary hover:underline mb-4" data-testid={`shop-fiche-${product.slug}`}>
          Découvrir la fiche <ArrowRight className="w-3 h-3" />
        </Link>
        {product.ingredients?.length > 0 && (
          <div className="text-xs text-muted-foreground mb-5 border-t border-border pt-3">
            <span className="uppercase tracking-[0.2em] text-foreground/80">Composition : </span>
            {product.ingredients.join(" · ")}
          </div>
        )}

        <div className="flex items-center justify-between gap-3">
          <div>
            <div className="font-display text-3xl gold-text">{product.price.toFixed(2)} €</div>
            <div className="text-[11px] uppercase tracking-[0.15em] text-muted-foreground">
              {product.format_label || `${product.volume_ml} ml · ${product.abv}% vol`}
            </div>
          </div>
          {quantity === 0 ? (
            <Button
              onClick={onInc}
              className="rounded-full bg-primary text-primary-foreground hover:bg-primary/90 h-11 px-5"
              data-testid={`shop-add-${product.slug}`}
            >
              <ShoppingBag className="w-4 h-4 mr-2" /> Ajouter
            </Button>
          ) : (
            <div className="flex items-center gap-3 rounded-full border border-primary/60 h-11 px-2">
              <button onClick={onDec} className="w-8 h-8 rounded-full hover:bg-primary/10 inline-flex items-center justify-center" data-testid={`shop-dec-${product.slug}`}>
                <Minus className="w-4 h-4" />
              </button>
              <span className="font-display text-lg w-6 text-center" data-testid={`shop-qty-${product.slug}`}>{quantity}</span>
              <button onClick={onInc} className="w-8 h-8 rounded-full hover:bg-primary/10 inline-flex items-center justify-center" data-testid={`shop-inc-${product.slug}`}>
                <Plus className="w-4 h-4" />
              </button>
            </div>
          )}
        </div>
      </div>
    </article>
  );
}

function CheckoutDrawer({ items, subtotal, onClose, onSuccess }) {
  const [mode, setMode] = useState("pickup");
  const [carrier, setCarrier] = useState("colissimo");
  const [carriers, setCarriers] = useState([]);
  const [bottleWeightKg, setBottleWeightKg] = useState(1.6);
  const [maxWeightKg, setMaxWeightKg] = useState(15);
  const [form, setForm] = useState({ name: "", email: "", phone: "", address: "" });
  const [acceptCgv, setAcceptCgv] = useState(false);
  const [acceptAdult, setAcceptAdult] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    api.get("/shipping/carriers").then(({ data }) => {
      setCarriers(data.carriers);
      if (data.bottle_weight_kg) setBottleWeightKg(data.bottle_weight_kg);
      if (data.max_weight_kg) setMaxWeightKg(data.max_weight_kg);
      // Ensure default carrier exists in response
      if (data.carriers?.length && !data.carriers.find((c) => c.id === "colissimo")) {
        setCarrier(data.carriers[0].id);
      }
    }).catch(() => {});
  }, []);

  const totalQty = items.reduce((s, i) => s + i.quantity, 0);
  const totalWeightKg = Math.round(totalQty * bottleWeightKg * 100) / 100;
  const overweight = mode === "shipping" && totalWeightKg > maxWeightKg;

  const computeFee = (carrierId) => {
    if (mode !== "shipping") return 0;
    if (totalWeightKg > maxWeightKg) return 0;
    const c = carriers.find((x) => x.id === carrierId);
    if (!c) return 0;
    for (const tier of c.tiers) {
      if (totalWeightKg <= tier.max_weight_kg) return tier.price;
    }
    return c.tiers[c.tiers.length - 1]?.price || 0;
  };

  const shippingFee = computeFee(carrier);
  const total = subtotal + shippingFee;

  const submit = async (e) => {
    e?.preventDefault();
    if (!form.name || !form.email) {
      toast.error("Nom et email requis");
      return;
    }
    if (mode === "shipping" && !form.address) {
      toast.error("Adresse de livraison requise");
      return;
    }
    if (overweight) {
      toast.error("Commande > 15 kg : merci de nous contacter pour un devis (contact@saveursnormandes.fr)");
      return;
    }
    if (!acceptAdult) {
      toast.error("Vous devez certifier être majeur(e) pour commander de l'alcool");
      return;
    }
    if (!acceptCgv) {
      toast.error("Vous devez accepter les CGV pour valider la commande");
      return;
    }
    setSubmitting(true);
    try {
      const origin = window.location.origin;
      const { data } = await api.post("/products/checkout", {
        lines: items.map((i) => ({ product_id: i.id, quantity: i.quantity })),
        customer_name: form.name,
        customer_email: form.email,
        customer_phone: form.phone || null,
        delivery_mode: mode,
        carrier: mode === "shipping" ? carrier : null,
        shipping_address: mode === "shipping" ? form.address : null,
        origin_url: origin,
      });
      onSuccess?.();
      window.location.href = data.url;
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur lors du paiement");
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end lg:items-center justify-center bg-black/70 backdrop-blur-sm p-0 lg:p-6" onClick={onClose}>
      <div
        onClick={(e) => e.stopPropagation()}
        className="w-full lg:max-w-2xl max-h-[92vh] overflow-y-auto rounded-t-3xl lg:rounded-2xl border border-border bg-card p-6 lg:p-8"
        data-testid="shop-checkout-drawer"
      >
        <div className="flex items-center justify-between mb-6">
          <h2 className="font-display text-3xl">Ma commande</h2>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground text-sm">Fermer ✕</button>
        </div>

        {/* Lignes */}
        <div className="space-y-3 mb-6">
          {items.map((i) => (
            <div key={i.id} className="flex items-center justify-between text-sm border-b border-border pb-3">
              <div>
                <div className="font-medium">{i.name}</div>
                <div className="text-muted-foreground text-xs">{i.quantity} × {i.price.toFixed(2)} €</div>
              </div>
              <div className="gold-text font-display text-lg">{i.line_total.toFixed(2)} €</div>
            </div>
          ))}
        </div>

        {/* Mode */}
        <div className="mb-6">
          <Label className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Mode de récupération</Label>
          <div className="grid grid-cols-2 gap-3 mt-3">
            <button
              type="button"
              onClick={() => setMode("pickup")}
              className={`rounded-xl border p-4 text-left transition-colors ${mode === "pickup" ? "border-primary bg-primary/5" : "border-border"}`}
              data-testid="shop-mode-pickup"
            >
              <Store className="w-5 h-5 text-primary mb-2" />
              <div className="font-medium">Retrait au bar</div>
              <div className="text-xs text-muted-foreground">4 rue de la Laiterie — gratuit</div>
            </button>
            <button
              type="button"
              onClick={() => setMode("shipping")}
              className={`rounded-xl border p-4 text-left transition-colors ${mode === "shipping" ? "border-primary bg-primary/5" : "border-border"}`}
              data-testid="shop-mode-shipping"
            >
              <Truck className="w-5 h-5 text-primary mb-2" />
              <div className="font-medium">Livraison</div>
              <div className="text-xs text-muted-foreground">
                {carriers.length
                  ? `Colissimo Pro · dès ${Math.min(...carriers.flatMap(c => c.tiers.map(t => t.price))).toFixed(2)} €`
                  : "Colissimo Pro"}
              </div>
            </button>
          </div>
        </div>

        {/* Sélection transporteur */}
        {mode === "shipping" && (
          <div className="mb-6">
            <div className="flex items-center justify-between mb-3">
              <Label className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Transporteur</Label>
              <span className="text-xs text-muted-foreground">
                Poids total : <strong className="text-foreground">{totalWeightKg.toFixed(2)} kg</strong>
              </span>
            </div>
            {overweight && (
              <div
                className="mb-3 rounded-xl border border-destructive/60 bg-destructive/10 p-4 text-sm text-destructive"
                data-testid="shop-overweight-notice"
              >
                Votre commande dépasse <strong>{maxWeightKg} kg</strong> ({totalWeightKg.toFixed(2)} kg).
                Merci de nous contacter à <a href="mailto:contact@saveursnormandes.fr" className="underline">contact@saveursnormandes.fr</a> pour un devis personnalisé.
              </div>
            )}
            <div className="space-y-2">
              {carriers.map((c) => {
                const fee = computeFee(c.id);
                const isSelected = carrier === c.id;
                return (
                  <button
                    key={c.id}
                    type="button"
                    onClick={() => setCarrier(c.id)}
                    disabled={overweight}
                    className={`w-full rounded-xl border p-4 text-left transition-colors flex items-center justify-between gap-4 disabled:opacity-50 disabled:cursor-not-allowed ${isSelected ? "border-primary bg-primary/5" : "border-border hover:border-primary/50"}`}
                    data-testid={`shop-carrier-${c.id}`}
                  >
                    <div className="flex items-center gap-4 min-w-0">
                      <div className={`w-5 h-5 rounded-full border-2 shrink-0 ${isSelected ? "border-primary bg-primary" : "border-border"}`} />
                      <div className="min-w-0">
                        <div className="font-medium">{c.label}</div>
                        <div className="text-xs text-muted-foreground">{c.eta}</div>
                      </div>
                    </div>
                    <div className="text-right shrink-0">
                      {overweight ? (
                        <div className="text-xs text-muted-foreground">Devis requis</div>
                      ) : (
                        <div className="font-display text-lg">{fee.toFixed(2)} €</div>
                      )}
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* Coordonnées */}
        <form onSubmit={submit} className="space-y-4">
          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <Label htmlFor="cust-name">Nom complet *</Label>
              <Input id="cust-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required className="bg-input mt-1.5" data-testid="shop-customer-name" />
            </div>
            <div>
              <Label htmlFor="cust-email">Email *</Label>
              <Input id="cust-email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} required className="bg-input mt-1.5" data-testid="shop-customer-email" />
            </div>
          </div>
          <div>
            <Label htmlFor="cust-phone">Téléphone</Label>
            <Input id="cust-phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="bg-input mt-1.5" data-testid="shop-customer-phone" />
          </div>
          {mode === "shipping" && (
            <div>
              <Label htmlFor="cust-addr">Adresse de livraison *</Label>
              <Textarea id="cust-addr" rows={3} value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} required className="bg-input mt-1.5" placeholder="N° & rue, ville, code postal, pays" data-testid="shop-customer-address" />
            </div>
          )}

          {/* Totaux */}
          <div className="border-t border-border pt-4 space-y-2 text-sm">
            <div className="flex justify-between"><span className="text-muted-foreground">Sous-total ({totalQty} bouteille{totalQty > 1 ? "s" : ""})</span><span>{subtotal.toFixed(2)} €</span></div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">
                Livraison{mode === "shipping" ? ` · ${totalWeightKg.toFixed(2)} kg` : ""}
              </span>
              <span>
                {mode !== "shipping" ? "—" : overweight ? "Devis" : `${shippingFee.toFixed(2)} €`}
              </span>
            </div>
            <div className="flex justify-between font-display text-2xl pt-2"><span>Total</span><span className="gold-text">{overweight ? "—" : `${total.toFixed(2)} €`}</span></div>
          </div>

          {/* Upsell — visible dès qu'un article est au panier boutique */}
          <div className="border-t border-border pt-4">
            <UpsellStrip context="shop_cart" cartTotal={subtotal} limit={2} compact />
          </div>

          {/* Consentements légaux */}
          <div className="space-y-3 border-t border-border pt-4">
            <label className="flex items-start gap-3 text-sm cursor-pointer" data-testid="shop-accept-adult-label">
              <input
                type="checkbox"
                checked={acceptAdult}
                onChange={(e) => setAcceptAdult(e.target.checked)}
                className="mt-1 w-4 h-4 accent-primary cursor-pointer"
                data-testid="shop-accept-adult"
              />
              <span className="text-muted-foreground">
                Je certifie sur l'honneur être <strong className="text-foreground">majeur(e) (18 ans et plus)</strong>. La vente d'alcool aux mineurs est interdite par la loi.
              </span>
            </label>
            <label className="flex items-start gap-3 text-sm cursor-pointer" data-testid="shop-accept-cgv-label">
              <input
                type="checkbox"
                checked={acceptCgv}
                onChange={(e) => setAcceptCgv(e.target.checked)}
                className="mt-1 w-4 h-4 accent-primary cursor-pointer"
                data-testid="shop-accept-cgv"
              />
              <span className="text-muted-foreground">
                J'ai lu et j'accepte les{" "}
                <a href="/cgv" target="_blank" rel="noopener noreferrer" className="text-primary underline">Conditions Générales de Vente</a>
                {" "}et la{" "}
                <a href="/confidentialite" target="_blank" rel="noopener noreferrer" className="text-primary underline">Politique de confidentialité</a>.
                Je comprends que <strong className="text-foreground">le droit de rétractation ne s'applique pas aux boissons alcoolisées</strong> (Art. L.221-28 Code consommation).
              </span>
            </label>
          </div>

          <Button type="submit" disabled={submitting || overweight || !acceptAdult || !acceptCgv} className="w-full rounded-full h-12 bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-50" data-testid="shop-checkout-submit">
            {submitting ? "Redirection vers le paiement…" : overweight ? "Devis requis (>15 kg)" : `Payer ${total.toFixed(2)} €`}
          </Button>
          <p className="text-[11px] text-muted-foreground text-center">
            Paiement sécurisé par Stripe · Vente interdite aux mineurs · L'abus d'alcool est dangereux pour la santé, à consommer avec modération.
          </p>
        </form>
      </div>
    </div>
  );
}
